import React, { useState } from 'react';
import * as ngeohash from 'ngeohash';

const Autocomplete = ({
  label,
  placeholder,
  onSelect,
  locations,
  className,
  disabled,
  showStations = true,
  showGooglePlaces = false,
  showFerries = false,
  region = 'GB',
  value = '',
  clearOnFocus = true
}) => {
  const [filteredStationResults, setFilteredStationResults] = useState([]);
  const [filteredGoogleResults, setGoogleResults] = useState([]);
  const [filteredFerriesResults, setFerriesResults] = useState([]);
  const [isFocused, setIsFocused] = useState(false);

  const searchLocations = (searchTerm) => {
    if (searchTerm.length < 3) {
      setFilteredStationResults([]);
      setGoogleResults([]);
      setFerriesResults([]);
      return;
    }

    // Simple filter since we don't have Fuse.js
    const results = locations.filter(location => 
      location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (location.code && location.code.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (showStations) {
      setFilteredStationResults(results);
    }

    if (showGooglePlaces) {
      searchGooglePlaces(searchTerm);
    }

    if (showFerries) {
      const ferryResults = locations.filter(location => 
        location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (location.code && location.code.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFerriesResults(ferryResults);
    }
  };

  const searchGooglePlaces = async (searchTerm) => {
    try {
      if (!window.google?.maps?.places?.AutocompleteService) {
        console.warn('Google Places API not loaded');
        setGoogleResults([]);
        return;
      }

      const googleAutocompleteService = new window.google.maps.places.AutocompleteService();
      const countryCodes = [region.toUpperCase()];

      const request = {
        input: searchTerm,
        componentRestrictions: { country: countryCodes },
      };

      const response = await new Promise((resolve) => {
        googleAutocompleteService.getPlacePredictions(request, (results) => {
          resolve(results || []);
        });
      });

      setGoogleResults(response);
    } catch (e) {
      console.error('Error getting Google place locations:', e);
      setGoogleResults([]);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    onSelect({
      name: newValue,
      stopCode: '',
      stopCodeType: ''
    });
    searchLocations(newValue);
  };

  const handleSelect = (item, type) => {
    let location;

    if (type === 'google') {
      if (!window.google?.maps?.places?.PlacesService) {
        console.warn('Google Places API not loaded');
        return;
      }

      const googlePlacesService = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );

      googlePlacesService.getDetails(
        { placeId: item.place_id },
        (response) => {
          if (!response?.geometry?.location) return;

          const lat = response.geometry.location.lat();
          const lon = response.geometry.location.lng();
          const geohashCode = ngeohash.encode(lat, lon);

          let selectedPlace = {
            name: response.name || '',
            geohashCode
          };

          location = {
            name: selectedPlace.name,
            stopCode: selectedPlace.geohashCode,
            stopCodeType: 'GEOHASH',
            type: 'google',
            latitude: lat,
            longitude: lon
          };
          onSelect(location);
        }
      );
    } else {
      location = {
        ...item,
        stopCode: item.code,
        stopCodeType: item.codeType,
        type: type
      };
      onSelect(location);
    }

    setFilteredStationResults([]);
    setGoogleResults([]);
    setFerriesResults([]);
  };

  return (
    <div className="relative">
      {label && <label className="block mb-2 text-sm font-medium">{label}</label>}
      <input
        type="text"
        className={`w-full p-[0.7rem] border-none rounded-md focus:outline-none focus:ring-2 autocomplete-input-large ${className || ''}`}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => {
          setIsFocused(true);
          if (clearOnFocus && value.length > 0) {
            onSelect({
              name: '',
              stopCode: '',
              stopCodeType: ''
            });
          }
        }}
        onBlur={() => setTimeout(() => setIsFocused(false), 200)}
      />

      {isFocused && (filteredStationResults.length > 0 || filteredGoogleResults.length > 0 || filteredFerriesResults.length > 0) && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg">
          {filteredStationResults.map((location, index) => (
            <div
              key={`station-${location.stopCode || index}`}
              className="p-2 cursor-pointer border-b border-gray-200"
              onClick={() => handleSelect(location, 'station')}
            >
              <div className="flex flex-row gap-6 items-center">
                <i className="flex items-center justify-center rounded-full zip-icon-mode_rail bg-journeyAlertsPurple text-white w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-xs sm:text-sm lg:text-base"></i>
                <p className="text-base sm:text-lg"> {location.name}</p>
              </div>
            </div>
          ))}

          {filteredGoogleResults.map((result, index) => (
            <div
              key={`google-${result.place_id || index}`}
              className="p-2 cursor-pointer border-b border-gray-200"
              onClick={() => handleSelect(result, 'google')}
            >
              <p className="text-base sm:text-lg flex items-center gap-2">
                <span className="zip-icon-poi_generic" style={{ color: 'var(--secondary-color-button)' }}></span>
                {result.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {isFocused && value.length > 0 && value.length < 3 && (
        <div className="absolute z-10 w-full mt-1 p-2 bg-white border rounded-md text-sm text-gray-500">
          Please enter at least 3 characters
        </div>
      )}
    </div>
  );
};

export default Autocomplete;